if (!customElements.get("lumihi-tabs")) {
  customElements.define(
    "lumihi-tabs",
    class extends HTMLElement {
      connectedCallback() {
        this.tabs = [...this.querySelectorAll('[role="tab"]')];
        this.tabs.forEach((tab) =>
          tab.addEventListener("click", () => this.select(tab)),
        );
      }
      select(active) {
        this.tabs.forEach((tab) => {
          const selected = tab === active;
          tab.setAttribute("aria-selected", selected);
          tab.tabIndex = selected ? 0 : -1;
          const panel = this.querySelector(
            `#${tab.getAttribute("aria-controls")}`,
          );
          if (panel) panel.hidden = !selected;
        });
      }
    },
  );
}
if (!customElements.get("lumihi-slideshow")) {
  customElements.define(
    "lumihi-slideshow",
    class extends HTMLElement {
      connectedCallback() {
        if (this.slideshowReady) return;
        this.slideshowReady = true;
        this.slides = [...this.querySelectorAll("[data-lm-slide]")];
        this.dots = [...this.querySelectorAll("[data-lm-dot]")];
        this.index = Math.max(
          0,
          this.slides.findIndex((slide) =>
            slide.classList.contains("is-active"),
          ),
        );
        this.querySelector("[data-lm-prev]")?.addEventListener("click", () =>
          this.show(this.index - 1, true),
        );
        this.querySelector("[data-lm-next]")?.addEventListener("click", () =>
          this.show(this.index + 1, true),
        );
        this.dots.forEach((dot, index) =>
          dot.addEventListener("click", () => this.show(index, true)),
        );
        this.addEventListener(
          "touchstart",
          (event) => {
            this.touchStartX = event.touches[0]?.clientX;
            this.touchStartY = event.touches[0]?.clientY;
            this.touchAxis = null;
          },
          { passive: true },
        );
        this.addEventListener(
          "touchmove",
          (event) => {
            if (this.touchStartX == null || this.touchStartY == null) return;
            const touch = event.touches[0];
            const distanceX = touch.clientX - this.touchStartX;
            const distanceY = touch.clientY - this.touchStartY;
            if (
              !this.touchAxis &&
              Math.max(Math.abs(distanceX), Math.abs(distanceY)) > 8
            ) {
              this.touchAxis =
                Math.abs(distanceX) > Math.abs(distanceY) * 1.15 ? "x" : "y";
            }
            if (this.touchAxis === "x") event.preventDefault();
          },
          { passive: false },
        );
        this.addEventListener(
          "touchend",
          (event) => {
            if (this.touchStartX == null || this.touchStartY == null) return;
            const distanceX =
              event.changedTouches[0]?.clientX - this.touchStartX;
            const distanceY =
              event.changedTouches[0]?.clientY - this.touchStartY;
            this.touchStartX = null;
            this.touchStartY = null;
            const isHorizontal =
              this.touchAxis === "x" ||
              Math.abs(distanceX) > Math.abs(distanceY) * 1.15;
            this.touchAxis = null;
            if (!isHorizontal || Math.abs(distanceX) < 45) return;
            this.show(this.index + (distanceX < 0 ? 1 : -1), true);
          },
          { passive: true },
        );
        this.addEventListener(
          "touchcancel",
          () => {
            this.touchStartX = null;
            this.touchStartY = null;
            this.touchAxis = null;
          },
          { passive: true },
        );
        if (this.dataset.pauseHover === "true") {
          this.addEventListener("mouseenter", () => this.stop());
          this.addEventListener("mouseleave", () => this.start());
        }
        this.start();
      }
      show(index, restart = false) {
        if (this.slides.length < 2) return;
        this.index = (index + this.slides.length) % this.slides.length;
        this.slides.forEach((slide, i) => {
          slide.classList.toggle("is-active", i === this.index);
          slide.setAttribute("aria-hidden", i !== this.index);
          const video = slide.querySelector("video");
          if (video)
            i === this.index ? video.play().catch(() => {}) : video.pause();
        });
        this.dots.forEach((dot, i) =>
          dot.setAttribute("aria-current", i === this.index),
        );
        if (restart) {
          this.stop();
          this.start();
        }
      }
      start() {
        if (
          this.dataset.autoplay !== "true" ||
          this.slides.length < 2 ||
          matchMedia("(prefers-reduced-motion: reduce)").matches
        )
          return;
        this.stop();
        this.timer = setInterval(
          () => this.show(this.index + 1),
          Number(this.dataset.speed || 5) * 1000,
        );
      }
      stop() {
        clearInterval(this.timer);
      }
      disconnectedCallback() {
        this.stop();
        this.slideshowReady = false;
      }
    },
  );
}

if (!customElements.get("lumihi-room")) {
  customElements.define(
    "lumihi-room",
    class extends HTMLElement {
      connectedCallback() {
        if (this.roomReady) return;
        this.roomReady = true;
        this.controller = new AbortController();
        const { signal } = this.controller;
        const closeSpot = (spot) => {
          if (!spot) return;
          spot.classList.remove("is-open");
          spot
            .querySelector(".lm-hotspot__button")
            ?.setAttribute("aria-expanded", "false");
          spot
            .querySelector(".lm-hotspot__card")
            ?.setAttribute("aria-hidden", "true");
        };
        this.addEventListener(
          "click",
          (event) => {
            const button = event.target.closest(".lm-hotspot__button");
            if (!button) return;
            const spot = button.closest(".lm-hotspot");
            const willOpen = !spot.classList.contains("is-open");
            this.querySelectorAll(".lm-hotspot.is-open").forEach(closeSpot);
            spot.classList.toggle("is-open", willOpen);
            button.setAttribute("aria-expanded", String(willOpen));
            spot
              .querySelector(".lm-hotspot__card")
              ?.setAttribute("aria-hidden", String(!willOpen));
          },
          { signal },
        );
        document.addEventListener(
          "pointerdown",
          (event) => {
            if (!this.contains(event.target))
              this.querySelectorAll(".lm-hotspot.is-open").forEach(closeSpot);
            else if (!event.target.closest(".lm-hotspot"))
              this.querySelectorAll(".lm-hotspot.is-open").forEach(closeSpot);
          },
          { signal, capture: true },
        );
        document.addEventListener(
          "keydown",
          (event) => {
            if (event.key !== "Escape") return;
            const openSpot = this.querySelector(".lm-hotspot.is-open");
            if (!openSpot) return;
            const button = openSpot.querySelector(".lm-hotspot__button");
            closeSpot(openSpot);
            button?.focus();
          },
          { signal },
        );
      }
      disconnectedCallback() {
        this.controller?.abort();
        this.roomReady = false;
      }
    },
  );
}
if (!customElements.get("lumihi-popup")) {
  customElements.define(
    "lumihi-popup",
    class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.controller = new AbortController();
        const { signal } = this.controller;
        this.key = `lumihi-popup-${this.dataset.section}`;
        this.querySelectorAll("[data-popup-close]").forEach((element) => {
          element.addEventListener(
            "click",
            (event) => {
              event.preventDefault();
              this.close(!Shopify.designMode);
            },
            { signal },
          );
        });
        document.addEventListener(
          "keydown",
          (event) => {
            if (event.key === "Escape" && !this.hidden)
              this.close(!Shopify.designMode);
          },
          { signal },
        );

        if (Shopify.designMode) {
          this.open();
          document.addEventListener(
            "shopify:section:select",
            (event) => {
              if (event.detail.sectionId === this.dataset.section) this.open();
            },
            { signal },
          );
          document.addEventListener(
            "shopify:section:deselect",
            (event) => {
              if (event.detail.sectionId === this.dataset.section)
                this.close(false);
            },
            { signal },
          );
          return;
        }

        const last = Number(localStorage.getItem(this.key) || 0);
        const cooldown = Number(this.dataset.days || 0) * 86400000;
        if (!last || Date.now() - last > cooldown) {
          this.timer = setTimeout(
            () => this.open(),
            Number(this.dataset.delay || 0) * 1000,
          );
        }
      }
      open() {
        clearTimeout(this.timer);
        this.previouslyFocused = document.activeElement;
        this.hidden = false;
        this.setAttribute("aria-hidden", "false");
        document.body.classList.add("overflow-hidden");
        requestAnimationFrame(() =>
          this.querySelector(".lm-popup__close")?.focus(),
        );
      }
      close(remember = true) {
        clearTimeout(this.timer);
        this.hidden = true;
        this.setAttribute("aria-hidden", "true");
        if (!document.querySelector("lumihi-popup:not([hidden])"))
          document.body.classList.remove("overflow-hidden");
        if (remember) localStorage.setItem(this.key, Date.now());
        if (
          this.previouslyFocused instanceof HTMLElement &&
          document.contains(this.previouslyFocused)
        )
          this.previouslyFocused.focus();
      }
      disconnectedCallback() {
        clearTimeout(this.timer);
        this.controller?.abort();
        if (!document.querySelector("lumihi-popup:not([hidden])"))
          document.body.classList.remove("overflow-hidden");
      }
    },
  );
}
